

import React, { useEffect, useRef, useState } from "react";
import { LabelModes } from "../../../../ui-components/molecules/labeled-inputs/labeledInput";
import { useSelector } from "react-redux";
import { terminalDetailsSelector } from "../../../../slices/terminal-summary/terminalDetailsSlice";
import { TerminalState } from "../../../../slices/terminal-summary/TerminalDetailsState";
import ActionSection from "features/terminal-details/common/actionSection";
import XGSFormInput from "../../../../ui-components/form/input/xgsFormInput";
import XGSFormSelect from "../../../../ui-components/form/select/xgsFormSelect";
import { XGSSelectOption } from "../../../../ui-components/xgs-select/xgsSelect";
import { Form, Formik, FormikProps } from "formik";
import { BasicInfo, getBasicInfoValidation } from "../../../../app/data/terminal-details/models";
import { divisionOptions, terminalTypeOptions, timezoneOptions, WMSWarehousingOptions } from "app/data/terminal-details/models";
import _ from "lodash";
import { useDispatch } from "react-redux";
import { updateBasicInfoSelector, updateTerminalBasicInfoById } from "../../../../slices/terminal-summary/basicInfoSlice";
import { UpdateBasicInfoState } from "../../../../slices/terminal-summary/BasicInfoState";
import HoursOfOperationForm from "features/common/form/hoursOfOperations";
import '../basicInfo.scss'
import { getRegionsServiceCenters, serviceCentersSelector } from "slices/service-centers/serviceCentersSlice";
import ServiceCentersState from "slices/service-centers/ServiceCentersState";
import { updateStatusSelector, updateTerminalStatus } from "slices/terminal-summary/terminalStatusSlice";
import { getTerminalSummaryByTerminalNumber } from "slices/terminal-summary/terminalDetailsSlice";
import { UpdateStatusState } from "slices/terminal-summary/TerminalStatusState";
import { toast } from "react-toastify";
import XGSToggle from "ui-components/xgs-toggle/xgs-toggle";

const EditGeneralInfo = ({ onClose }: { onClose: () => void }): React.ReactElement => {
    const [spinner, setSpinner] = useState<boolean>(false);
    const terminalState: TerminalState = useSelector(terminalDetailsSelector);
    const updateBasicInfoState: UpdateBasicInfoState = useSelector(updateBasicInfoSelector);
    const serviceCentersState: ServiceCentersState = useSelector(serviceCentersSelector);
    const updateStatusState: UpdateStatusState = useSelector(updateStatusSelector);
    const [activeTerminal, setActiveTerminal] = useState<boolean>(false);
    const dispatch = useDispatch();
    const initialValues: BasicInfo = {
        terminalNumber: terminalState.data?.terminalNumber?.toString() || "",
        terminalType: terminalTypeOptions.find((option) => option.value === terminalState.data?.terminalType?.type)?.value?.toString() || "",
        division: divisionOptions.find((option) => option.label === terminalState.data?.division)?.label || "",
        timezone: terminalState.data?.timezone || "",
        airportCode: terminalState.data?.airportCode || "",
        status: terminalState.data?.status || "",
        region: terminalState.data?.region?.regionName || "",
        wmsWarehousing: terminalState.data?.warehouse ? 'Yes' : 'No',
        operationOpenHours: terminalState.data?.terminalOpenHoursList.filter((item: any) => item.type === "OPERATION").map((item: any) => item.openHours).join(", ") || 'Monday-Friday: --:-- - --:--',
        willCallOpenHours: terminalState.data?.terminalOpenHoursList.filter((item: any) => item.type === "WILL_CALL").map((item: any) => item.openHours).join(", ") || 'Monday-Friday: --:-- - --:--',
        operationFromTime: terminalState.data?.terminalOpenHoursList?.filter((item) => item.type === "OPERATION")?.map((item: any) => item.fromTime).join(", ") || null,
        operationToTime: terminalState.data?.terminalOpenHoursList?.filter((item) => item.type === "OPERATION")?.map((item: any) => item.toTime).join(", ") || null,
        willCallFromTime: terminalState.data?.terminalOpenHoursList?.filter((item) => item.type === "WILL_CALL")?.map((item: any) => item.fromTime).join(", ") || null,
        willCallToTime: terminalState.data?.terminalOpenHoursList?.filter((item) => item.type === "WILL_CALL")?.map((item: any) => item.toTime).join(", ") || null,
    };
    const formRef = useRef<FormikProps<BasicInfo>>(null);

    const handleSpinner = () => {
        setTimeout(() => {
            setSpinner(false);
        }, 1000);
    };

    const handleActive = (activeTerminal: boolean) => {
        dispatch(updateTerminalStatus(terminalState.data?.terminalNumber?.toString() || "", 
        { status: activeTerminal ? "ACTIVE" : "INACTIVE" }, () => {
            toast.info("Updated details");
        }, (message) => {
            toast.error(message);
        }));
    }

    const handleClear = () => {
        formRef.current?.resetForm();
        setActiveTerminal(terminalState.data?.status === "ACTIVE");
    };

    useEffect(() => {
        setActiveTerminal(terminalState.data?.status === "ACTIVE");
    }, [terminalState.data]);

    useEffect(() => {
        dispatch(getRegionsServiceCenters());
    }, [dispatch]);

    useEffect(() => {
        if (updateBasicInfoState.isSuccess || updateStatusState.isSuccess) {
            dispatch(getTerminalSummaryByTerminalNumber(terminalState.data?.terminalNumber?.toString() || ""));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [updateBasicInfoState, updateStatusState, dispatch]);

    return (terminalState?.data ?
        <>
            <Formik
                onSubmit={() => { }}
                initialValues={initialValues}
                validationSchema={getBasicInfoValidation()}
                innerRef={formRef}
            >
                {(formikProps: FormikProps<BasicInfo>) => (
                    <Form>
                        <div className="edit-flex-box">
                            <div className="edit-flex-box__item">
                                <XGSFormInput
                                    type="text"
                                    name="terminalNumber"
                                    required={true}
                                    labelMode={LabelModes.column}
                                    label="Terminal Number"
                                    className="width-50"
                                    disabled
                                />
                                <XGSFormInput
                                    type="text"
                                    name="airportCode"
                                    required
                                    labelMode={LabelModes.column}
                                    label="Airport Code"
                                    className="width-50"
                                />
                            </div>
                            <div className="edit-flex-box__item">
                                <XGSFormSelect
                                    label="Terminal Type"
                                    name="terminalType"
                                    required={true}
                                    options={terminalTypeOptions}
                                    value={terminalTypeOptions.find(
                                        (option) => option.value === formikProps.values.terminalType
                                    )}
                                    onValueChange={(option) => {
                                        formikProps.setFieldValue("terminalType", option?.value ?? "");
                                    }}
                                    formik
                                    className="width-50"
                                    labelMode={LabelModes.column}
                                />
                                <XGSFormSelect
                                    label="Division"
                                    name="division"
                                    required={true}
                                    options={divisionOptions}
                                    value={divisionOptions.find(
                                        (option) => option.label === formikProps.values.division
                                    )}
                                    onValueChange={(option) => {
                                        formikProps.setFieldValue("division", option?.label ?? "");
                                    }}
                                    formik
                                    className="width-50"
                                    labelMode={LabelModes.column}
                                />
                            </div>
                            <div className="edit-flex-box__item">
                                <XGSFormSelect
                                    label="Region"
                                    name="region"
                                    required={true}
                                    options={serviceCentersState.regions?.map(
                                        (region): XGSSelectOption => ({
                                            label: region.regionName,
                                            value: region.id
                                        })
                                    ) || []}
                                    value={serviceCentersState.regions?.map(
                                        (region): XGSSelectOption => ({
                                            label: region.regionName,
                                            value: region.id
                                        })
                                    )?.find(
                                        (option) => option.label === formikProps.values.region
                                    )}
                                    onValueChange={(option) => {
                                        formikProps.setFieldValue("region", option?.label ?? "");
                                    }}
                                    formik
                                    className="width-50"
                                    labelMode={LabelModes.column}
                                />
                                <XGSFormSelect
                                    label="Timezone"
                                    name="timezone"
                                    required={true}
                                    options={timezoneOptions}
                                    value={timezoneOptions.find(
                                        (option) => option.value === formikProps.values.timezone
                                    )}
                                    onValueChange={(option) => {
                                        formikProps.setFieldValue("timezone", option?.value ?? "");
                                    }}
                                    formik
                                    className="width-50"
                                    labelMode={LabelModes.column}
                                />
                            </div>
                            <div className="edit-flex-box__item">
                                <XGSFormSelect
                                    label="WMS Warehousing"
                                    name="wmsWarehousing"
                                    required={true}
                                    options={WMSWarehousingOptions}
                                    value={WMSWarehousingOptions.find(
                                        (option) => option.value === formikProps.values.wmsWarehousing
                                    )}
                                    onValueChange={(option) => {
                                        formikProps.setFieldValue("wmsWarehousing", option?.value ?? "");
                                    }}
                                    formik
                                    className="width-50"
                                    labelMode={LabelModes.column}
                                />
                            </div>
                        </div>
                        <div className="margin-top-40">
                            <div className="sub-title">Hours of operation</div>
                            <HoursOfOperationForm />
                        </div>

                        <div className="action-section__toggle">
                            <XGSToggle
                                onChange={() => {
                                    setActiveTerminal(!activeTerminal);
                                    handleActive(!activeTerminal);
                                    formikProps.setFieldValue("status", !activeTerminal ? "ACTIVE" : "INACTIVE");
                                }}
                                checked={activeTerminal}
                                label="Active"
                            />
                            <>
                                <ActionSection
                                    handleClear={handleClear}
                                    handleSave={async () => {
                                        setSpinner(true);
                                        const values = formRef.current?.values;

                                        await Promise.all([
                                            formRef?.current?.submitForm(),
                                        ]);
                                        handleSpinner();

                                        if (terminalState.data?.terminalNumber?.toString() && _.isEmpty(formRef.current?.errors)) {
                                            dispatch(updateTerminalBasicInfoById(terminalState.data?.terminalNumber?.toString() || "", {
                                                terminalType: values?.terminalType || "",
                                                regionName: values?.region || "",
                                                airportCode: values?.airportCode || "",
                                                status: activeTerminal ? "ACTIVE" : "INACTIVE",
                                                division: values?.division || "",
                                                warehouse: values?.wmsWarehousing === "Yes",
                                                timezone: values?.timezone || "",
                                                operationDays: "Monday-Friday",
                                                operationFromTime: values?.operationFromTime || null,
                                                operationToTime: values?.operationToTime || null,
                                                willCallDays: "Monday-Friday",
                                                willCallFromTime: values?.willCallFromTime || null,
                                                willCallToTime: values?.willCallToTime || null
                                            }, () => {
                                                toast.info("Updated details");
                                            }, (message) => {
                                                toast.error(message);
                                            }));
                                        }

                                    }}
                                    spinner={updateBasicInfoState.isLoading || updateStatusState.isLoading || spinner}
                                    disabled={!formikProps.dirty}
                                />
                            </>
                        </div>
                    </Form>)}
            </Formik>



        </> : <></>
    );
}
export default EditGeneralInfo;
